import React, { useState, useEffect } from 'react';
import { usersAPI, departmentAPI, designationAPI, googleSheetsAPI, groupAPI, idCardDesignAPI } from '../services/api';
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from '../utils/constants';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';
import { useGroup } from '../contexts/GroupContext';
import { useAuth } from '../contexts/AuthContext';

const SettingsPage = () => {
  const { groups, selectedGroupId } = useGroup();
  const { user, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  // Profile tab state
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: '',
    is_active: true
  });

  // Password reset state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Department state
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [editingDept, setEditingDept] = useState(null);
  const [loadingDept, setLoadingDept] = useState(false);

  // Designation state
  const [designations, setDesignations] = useState([]);
  const [newDesignation, setNewDesignation] = useState('');
  const [editingDesig, setEditingDesig] = useState(null);
  const [loadingDesig, setLoadingDesig] = useState(false);

  // Google Sheets state
  const [googleSheetsConfig, setGoogleSheetsConfig] = useState({
    spreadsheetId: '',
    gid: '',
    credentialsPath: '',
    defaultGroupId: selectedGroupId || ''
  });
  const [loadingGoogleSheets, setLoadingGoogleSheets] = useState(false);

  // ID Card Design state
  const [idCardDesign, setIdCardDesign] = useState({
    header_image_path: null,
    signature_image_path: null,
    footer_address: '',
    group_name: '',
  });
  const [loadingIdCardDesign, setLoadingIdCardDesign] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);

  // Fetch user profile
  useEffect(() => {
    fetchProfile();
    fetchDepartments();
    fetchDesignations();
    fetchGoogleSheetsConfig();
  }, []);

  // Update defaultGroupId when selectedGroupId changes
  useEffect(() => {
    if (selectedGroupId && !googleSheetsConfig.defaultGroupId) {
      setGoogleSheetsConfig(prev => ({ ...prev, defaultGroupId: selectedGroupId }));
    }
    // Also load ID card design when group changes
    if (selectedGroupId) {
      loadIdCardDesign(selectedGroupId);
    }
  }, [selectedGroupId, googleSheetsConfig.defaultGroupId]);

  // Also reload ID card design when switching into that tab
  useEffect(() => {
    if (activeTab === 'id-card-design' && selectedGroupId) {
      loadIdCardDesign(selectedGroupId);
    }
  }, [activeTab, selectedGroupId]);

  const fetchProfile = async () => {
    try {
      const response = await usersAPI.me();
      setProfile(response.data.data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      setLoadingDept(true);
      const response = await departmentAPI.getAll();
      setDepartments(response.data.data || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError('Failed to fetch departments');
    } finally {
      setLoadingDept(false);
    }
  };

  const fetchDesignations = async () => {
    try {
      setLoadingDesig(true);
      const response = await designationAPI.getAll();
      setDesignations(response.data.data || []);
    } catch (err) {
      console.error('Error fetching designations:', err);
      setError('Failed to fetch designations');
    } finally {
      setLoadingDesig(false);
    }
  };

  // Profile handlers
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      await usersAPI.update(profile.id, { name: profile.name });
      setSuccess(SUCCESS_MESSAGES.EMPLOYEE_UPDATED || 'Profile updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  // Password reset handlers
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await usersAPI.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setSuccess('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  // Department handlers
  const handleAddDepartment = async () => {
    if (!newDepartment.trim()) {
      setError('Department name is required');
      return;
    }
    try {
      setLoadingDept(true);
      setError(null);
      const response = await departmentAPI.create({ name: newDepartment.trim() });
      setNewDepartment('');
      setSuccess('Department added successfully');
      await fetchDepartments(); // Refresh list
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add department');
    } finally {
      setLoadingDept(false);
    }
  };

  const handleDeleteDepartment = async (dept) => {
    if (!window.confirm(`Are you sure you want to delete "${dept.name}"?`)) {
      return;
    }
    try {
      setLoadingDept(true);
      setError(null);
      await departmentAPI.delete(dept.id);
      setSuccess('Department deleted successfully');
      await fetchDepartments(); // Refresh list
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete department');
    } finally {
      setLoadingDept(false);
    }
  };

  const handleEditDepartment = (dept) => {
    setEditingDept(dept);
    setNewDepartment(dept.name);
  };

  const handleUpdateDepartment = async () => {
    if (!newDepartment.trim()) {
      setError('Department name is required');
      return;
    }
    try {
      setLoadingDept(true);
      setError(null);
      await departmentAPI.update(editingDept.id, { name: newDepartment.trim() });
      setNewDepartment('');
      setEditingDept(null);
      setSuccess('Department updated successfully');
      await fetchDepartments(); // Refresh list
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update department');
    } finally {
      setLoadingDept(false);
    }
  };

  // Designation handlers
  const handleAddDesignation = async () => {
    if (!newDesignation.trim()) {
      setError('Designation name is required');
      return;
    }
    try {
      setLoadingDesig(true);
      setError(null);
      const response = await designationAPI.create({ name: newDesignation.trim() });
      setNewDesignation('');
      setSuccess('Designation added successfully');
      await fetchDesignations(); // Refresh list
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add designation');
    } finally {
      setLoadingDesig(false);
    }
  };

  const handleDeleteDesignation = async (desig) => {
    if (!window.confirm(`Are you sure you want to delete "${desig.name}"?`)) {
      return;
    }
    try {
      setLoadingDesig(true);
      setError(null);
      await designationAPI.delete(desig.id);
      setSuccess('Designation deleted successfully');
      await fetchDesignations(); // Refresh list
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete designation');
    } finally {
      setLoadingDesig(false);
    }
  };

  const handleEditDesignation = (desig) => {
    setEditingDesig(desig);
    setNewDesignation(desig.name);
  };

  const handleUpdateDesignation = async () => {
    if (!newDesignation.trim()) {
      setError('Designation name is required');
      return;
    }
    try {
      setLoadingDesig(true);
      setError(null);
      await designationAPI.update(editingDesig.id, { name: newDesignation.trim() });
      setNewDesignation('');
      setEditingDesig(null);
      setSuccess('Designation updated successfully');
      await fetchDesignations(); // Refresh list
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update designation');
    } finally {
      setLoadingDesig(false);
    }
  };

  // Google Sheets functions
  const fetchGoogleSheetsConfig = async () => {
    try {
      setLoadingGoogleSheets(true);
      const response = await googleSheetsAPI.getConfig();
      if (response.data.data) {
        setGoogleSheetsConfig({
          spreadsheetId: response.data.data.spreadsheetId || '',
          gid: response.data.data.gid || '',
          credentialsPath: response.data.data.credentialsPath || '',
          defaultGroupId: response.data.data.defaultGroupId || selectedGroupId || ''
        });
      }
    } catch (err) {
      console.error('Error fetching Google Sheets config:', err);
    } finally {
      setLoadingGoogleSheets(false);
    }
  };

  const handleSaveGoogleSheetsConfig = async (e) => {
    e.preventDefault();
    try {
      setLoadingGoogleSheets(true);
      setError(null);
      await googleSheetsAPI.saveConfig(googleSheetsConfig);
      setSuccess('Google Sheets configuration saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save Google Sheets configuration');
    } finally {
      setLoadingGoogleSheets(false);
    }
  };

  const extractSheetIdFromUrl = (url) => {
    if (!url) return '';
    const match = url.match(/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : url;
  };

  const handleSheetIdChange = (e) => {
    const value = e.target.value;
    const sheetId = extractSheetIdFromUrl(value);
    setGoogleSheetsConfig(prev => ({ ...prev, spreadsheetId: sheetId }));
  };

  const canManageIdCardDesign = isSuperAdmin || user?.role === 'admin';

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <i className="fa-solid fa-address-card"></i>},
    { id: 'password', label: 'Reset Password', icon: <i className="fa-solid fa-lock"></i> },
    { id: 'department', label: 'Department', icon: <i className="fa-regular fa-building"></i> },
    { id: 'designation', label: 'Designation', icon: <i className="fa-solid fa-graduation-cap"></i> },
    ...(canManageIdCardDesign ? [{ id: 'id-card-design', label: 'ID Card Design', icon: <i className="fa-regular fa-id-card"></i> }] : []),
    { id: 'google-sheets', label: 'Google Sheets', icon: <i className="fa-solid fa-chart-column"></i> }
  ];

  // ID Card Design helpers
  const loadIdCardDesign = async (groupId) => {
    try {
      setLoadingIdCardDesign(true);
      const response = await idCardDesignAPI.getConfig(groupId);
      const data = response.data.data || {};
      setIdCardDesign({
        header_image_path: data.header_image_path || null,
        signature_image_path: data.signature_image_path || null,
        footer_address: data.footer_address || '',
        group_name: data.group_name || 'SRPF Group-1, Pune',
      });
    } catch (err) {
      console.error('Error fetching ID card design config:', err);
      setError('Failed to fetch ID card design configuration');
    } finally {
      setLoadingIdCardDesign(false);
    }
  };

  const handleSaveIdCardFooter = async (e) => {
    e.preventDefault();
    if (!selectedGroupId) {
      setError('Please select a group to configure ID card design');
      return;
    }
    try {
      setLoadingIdCardDesign(true);
      setError(null);
      await idCardDesignAPI.saveConfig(selectedGroupId, {
        footer_address: idCardDesign.footer_address,
        group_name: idCardDesign.group_name,
      });
      setSuccess('ID card footer address updated successfully');
      setTimeout(() => setSuccess(null), 10000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save ID card design');
    } finally {
      setLoadingIdCardDesign(false);
    }
  };

  const handleUploadIdCardHeader = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedGroupId) return;
    try {
      setUploadingHeader(true);
      setError(null);
      const response = await idCardDesignAPI.uploadHeader(selectedGroupId, file);
      const data = response.data.data;
      setIdCardDesign(prev => ({
        ...prev,
        header_image_path: data.header_image_path || prev.header_image_path,
      }));
      setSuccess('Header image uploaded successfully');
      setTimeout(() => setSuccess(null), 10000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload header image');
    } finally {
      setUploadingHeader(false);
      // Reset input value so same file can be selected again if needed
      e.target.value = '';
    }
  };

  const handleUploadIdCardSignature = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedGroupId) return;
    try {
      setUploadingSignature(true);
      setError(null);
      const response = await idCardDesignAPI.uploadSignature(selectedGroupId, file);
      const data = response.data.data;
      setIdCardDesign(prev => ({
        ...prev,
        signature_image_path: data.signature_image_path || prev.signature_image_path,
      }));
      setSuccess('Authority signature uploaded successfully');
      setTimeout(() => setSuccess(null), 10000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload authority signature');
    } finally {
      setUploadingSignature(false);
      e.target.value = '';
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h2>Settings</h2>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError(null)} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess(null)} />}

      <div className="settings-container">
        <div className="settings-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">
                {typeof tab.icon === 'function' ? React.createElement(tab.icon, { style: { width: '16px', height: '16px' } }) : tab.icon}
              </span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="settings-content">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="settings-tab-content">
              <h3>User Profile</h3>
              <form onSubmit={handleProfileUpdate} className="settings-form">
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input
                    type="text"
                    id="name"
                    className="form-control"
                    value={profile.name || ''}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    placeholder="Enter your name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    className="form-control"
                    value={profile.email || ''}
                    disabled
                    style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                  <small style={{ color: '#6c757d' }}>Email cannot be changed</small>
                </div>
                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <input
                    type="text"
                    id="role"
                    className="form-control"
                    value={profile.role || ''}
                    disabled
                    style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Updating...' : 'Update Profile'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ID Card Design Tab (Admins only) */}
          {activeTab === 'id-card-design' && canManageIdCardDesign && (
            <div className="settings-tab-content">
              <h3>ID Card Design</h3>
              <p className="settings-description" style={{ marginBottom: '1.5rem', color: '#64748b' }}>
                Configure the visual design of the ID card for the selected SRPF group. Employee details,
                QR code and DigiLocker logic remain unchanged and will continue to come from the database.
              </p>

              {!selectedGroupId && (
                <div className="alert alert-warning" role="alert" style={{ marginBottom: '1.5rem' }}>
                  Please select a group from the top group selector to configure its ID card design.
                </div>
              )}

              {selectedGroupId && (
                <>
                  <div className="idcard-design-grid" style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '2rem' }}>
                    {/* Left: Form controls */}
                    <div>
                      <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#1e293b' }}>
                        Assets & Footer
                      </h4>

                      <div className="form-group">
                        <label>Header Image (PNG/JPG)</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <input
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={handleUploadIdCardHeader}
                            disabled={uploadingHeader || loadingIdCardDesign}
                          />
                          {uploadingHeader && <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Uploading...</span>}
                        </div>
                        <small className="form-text" style={{ display: 'block', marginTop: '0.5rem', color: '#64748b', fontSize: '0.8rem' }}>
                          This image appears at the top of the card.
                        </small>
                      </div>

                      <div className="form-group" style={{ marginTop: '1.25rem' }}>
                        <label>Authority Signature (PNG/JPG)</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <input
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={handleUploadIdCardSignature}
                            disabled={uploadingSignature || loadingIdCardDesign}
                          />
                          {uploadingSignature && <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Uploading...</span>}
                        </div>
                        <small className="form-text" style={{ display: 'block', marginTop: '0.5rem', color: '#64748b', fontSize: '0.8rem' }}>
                          This signature is shown near the bottom of the card as the issuing authority.
                        </small>
                      </div>

                      <form onSubmit={handleSaveIdCardFooter} style={{ marginTop: '1.5rem' }}>
                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                          <label htmlFor="idcard-group-name">Group Name (Signature Block)</label>
                          <input
                            id="idcard-group-name"
                            type="text"
                            className="form-control"
                            value={idCardDesign.group_name || ''}
                            onChange={(e) =>
                              setIdCardDesign((prev) => ({
                                ...prev,
                                group_name: e.target.value,
                              }))
                            }
                            placeholder="Group Name (e.g., SRPF Group-1, Pune)"
                          />
                          <small className="form-text" style={{ display: 'block', marginTop: '0.5rem', color: '#64748b', fontSize: '0.8rem' }}>
                            This text appears just below &quot;Commandant&quot; in the signature block.
                          </small>
                        </div>

                        <div className="form-group">
                          <label htmlFor="idcard-footer-address">Footer Address</label>
                          <textarea
                            id="idcard-footer-address"
                            className="form-control"
                            rows="3"
                            value={idCardDesign.footer_address || ''}
                            onChange={(e) =>
                              setIdCardDesign((prev) => ({
                                ...prev,
                                footer_address: e.target.value,
                              }))
                            }
                            placeholder="The Commandant Office, SRPF Group-1, Ramtekdi, Hadpsar, Pune 411022. Ph. 020 - 26860250"
                          />
                          <small className="form-text" style={{ display: 'block', marginTop: '0.5rem', color: '#64748b', fontSize: '0.8rem' }}>
                            This text is shown in the footer area above the tricolor strip. Keep it short for best readability.
                          </small>
                        </div>

                        <div className="form-actions" style={{ marginTop: '1rem' }}>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loadingIdCardDesign}
                          >
                            {loadingIdCardDesign ? 'Saving...' : 'Save Design'}
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Right: Live preview */}
                    <div>
                      <h4 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#1e293b' }}>
                        Preview (Layout Only)
                      </h4>
                      <div
                        style={{
                          borderRadius: '0.75rem',
                          border: '1px solid #e5e7eb',
                          padding: '1rem',
                          background: '#f9fafb',
                        }}
                      >
                        <div
                          style={{
                            width: '270px',
                            height: '470px',
                            margin: '0 auto',
                            background: '#ffffff',
                            boxShadow: '0 10px 25px rgba(15,23,42,0.12)',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '0.75rem',
                          }}
                        >
                          {/* Top tricolor border (static) */}
                          <div style={{ height: '6px', background: '#1E3A8A' }} />
                          <div style={{ height: '6px', background: '#FFD700' }} />
                          <div style={{ height: '6px', background: '#DC143C' }} />

                          {/* Header image preview */}
                          <div style={{ padding: '8px 12px', textAlign: 'center' }}>
                            {idCardDesign.header_image_path ? (
                              <img
                                src={idCardDesignAPI.getAssetUrl(idCardDesign.header_image_path)}
                                alt="Header"
                                style={{ maxWidth: '100%', maxHeight: '80px', objectFit: 'contain' }}
                              />
                            ) : (
                              <div
                                style={{
                                  height: '60px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '0.75rem',
                                  color: '#9ca3af',
                                  border: '1px dashed #e5e7eb',
                                  borderRadius: '0.5rem',
                                }}
                              >
                                Header image not set
                              </div>
                            )}
                          </div>

                          {/* Simplified body placeholder */}
                          <div style={{ padding: '4px 16px', fontSize: '0.7rem', color: '#111827' }}>
                            <div
                              style={{
                                width: '78px',
                                height: '78px',
                                borderRadius: '0.25rem',
                                border: '1px solid #e5e7eb',
                                margin: '0 auto 6px',
                                background: '#f3f4f6',
                              }}
                            />
                            <div style={{ textAlign: 'center', fontWeight: 600, fontSize: '0.8rem', marginBottom: '4px' }}>
                              EMPLOYEE NAME
                            </div>
                            <div style={{ lineHeight: 1.3 }}>
                              <div>Rank : &nbsp; <span style={{ fontWeight: 500 }}>CONSTABLE</span></div>
                              <div>I-Card No. : &nbsp; <span style={{ fontWeight: 500 }}>ABC12345</span></div>
                              <div>Identification Mark : &nbsp; Mole on left leg</div>
                              <div>Date of Birth : &nbsp; 28-01-2026</div>
                              <div>Date of Joining : &nbsp; 26-01-2026</div>
                              <div>Date of Retirement : &nbsp; 30-01-2026</div>
                              <div>Blood Group : &nbsp; B+</div>
                            </div>
                          </div>

                          {/* Signature + QR placeholder */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 16px', alignItems: 'flex-end' }}>
                            <div
                              style={{
                                width: '68px',
                                height: '68px',
                                borderRadius: '0.25rem',
                                border: '1px solid #e5e7eb',
                                background: '#f3f4f6',
                              }}
                            />
                            <div style={{ textAlign: 'center' }}>
                              {idCardDesign.signature_image_path ? (
                                <img
                                  src={idCardDesignAPI.getAssetUrl(idCardDesign.signature_image_path)}
                                  alt="Authority signature"
                                  style={{ maxWidth: '82px', maxHeight: '32px', objectFit: 'contain' }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '82px',
                                    height: '32px',
                                    borderRadius: '0.25rem',
                                    border: '1px dashed #e5e7eb',
                                    background: '#f9fafb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '0.65rem',
                                    color: '#9ca3af',
                                  }}
                                >
                                  Signature
                                </div>
                              )}
                              <div style={{ fontSize: '0.6rem', marginTop: '2px', color: '#1E3A8A', fontWeight: 600 }}>Commandant</div>
                              <div style={{ fontSize: '0.55rem', color: '#1E3A8A' }}>
                                {idCardDesign.group_name || 'SRPF Group-1, Pune'}
                              </div>
                            </div>
                          </div>

                          {/* Footer tricolor + address */}
                          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                            <div style={{ padding: '0 12px 4px', background: '#ffffff' }}>
                              <div
                                style={{
                                  fontSize: '0.58rem',
                                  color: '#1E3A8A',
                                  fontWeight: 600,
                                  textAlign: 'center',
                                }}
                              >
                                {(idCardDesign.footer_address || 'The Commandant Office, SRPF Group-1, Ramtekdi, Hadpsar, Pune 411022. Ph. 020 - 26860250')
                                  .split('\n')
                                  .join(' ')}
                              </div>
                            </div>
                            <div style={{ height: '6px', background: '#1E3A8A' }} />
                            <div style={{ height: '6px', background: '#DC143C' }} />
                            <div style={{ height: '6px', background: '#FFD700' }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Reset Password Tab */}
          {activeTab === 'password' && (
            <div className="settings-tab-content">
              <h3>Reset Password</h3>
              <form onSubmit={handlePasswordReset} className="settings-form">
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password *</label>
                  <input
                    type="password"
                    id="currentPassword"
                    className="form-control"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                    placeholder="Enter current password"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="newPassword">New Password *</label>
                  <input
                    type="password"
                    id="newPassword"
                    className="form-control"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    minLength={6}
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    className="form-control"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    placeholder="Confirm new password"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Department Tab */}
          {activeTab === 'department' && (
            <div className="settings-tab-content">
              <h3>Department Management</h3>
              
              {/* Add New Department Section - First */}
              <div style={{ 
                background: '#f8fafc', 
                padding: '1.5rem', 
                borderRadius: '0.5rem', 
                border: '1px solid #e5e7eb',
                marginBottom: '2rem'
              }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                  {editingDept ? 'Edit Department' : 'Add New Department'}
                </h4>
                <div className="settings-form">
                  <div className="form-group" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: 0 }}>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="newDepartment">Department Name *</label>
                      <input
                        type="text"
                        id="newDepartment"
                        className="form-control"
                        value={newDepartment}
                        onChange={(e) => setNewDepartment(e.target.value)}
                        placeholder="Enter department name (e.g., Administration, IT Department)"
                        onKeyPress={(e) => e.key === 'Enter' && (editingDept ? handleUpdateDepartment() : handleAddDepartment())}
                        style={{ fontSize: '0.875rem' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {editingDept ? (
                        <>
                          <button type="button" className="btn btn-primary" onClick={handleUpdateDepartment} disabled={loadingDept}>
                            {loadingDept ? 'Updating...' : 'Update'}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={() => { setEditingDept(null); setNewDepartment(''); }}
                            disabled={loadingDept}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button type="button" className="btn btn-primary" onClick={handleAddDepartment} disabled={loadingDept}>
                          {loadingDept ? (
                                  'Adding...'
                                ) : (
                                  <>
                                    <i className="fa-solid fa-plus me-2"></i> Add Department
                                  </>
                           )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Department List Table */}
              <div>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                  Existing Departments ({departments.length})
                </h4>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Department Name</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingDept ? (
                        <tr>
                          <td colSpan="2" style={{ textAlign: 'center', color: '#6c757d', padding: '2rem' }}>
                            Loading departments...
                          </td>
                        </tr>
                      ) : departments.length === 0 ? (
                        <tr>
                          <td colSpan="2" style={{ textAlign: 'center', color: '#6c757d', padding: '2rem' }}>
                            No departments found. Add a department above to get started.
                          </td>
                        </tr>
                      ) : (
                        departments.map((dept) => (
                          <tr key={dept.id}>
                            <td style={{ fontWeight: 500 }}>{dept.name}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleEditDepartment(dept)}
                                  title="Edit Department"
                                  disabled={loadingDept}
                                >
                                  <i class="fa-solid fa-pen-to-square"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteDepartment(dept)}
                                  title="Delete Department"
                                  disabled={loadingDept}
                                >
                                   <i class="fa-solid fa-trash-can"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Designation Tab */}
          {activeTab === 'designation' && (
            <div className="settings-tab-content">
              <h3>Designation Management</h3>
              
              {/* Add New Designation Section - First */}
              <div style={{ 
                background: '#f8fafc', 
                padding: '1.5rem', 
                borderRadius: '0.5rem', 
                border: '1px solid #e5e7eb',
                marginBottom: '2rem'
              }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                  {editingDesig ? 'Edit Designation' : 'Add New Designation'}
                </h4>
                <div className="settings-form">
                  <div className="form-group" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: 0 }}>
                    <div style={{ flex: 1 }}>
                      <label htmlFor="newDesignation">Designation Name *</label>
                      <input
                        type="text"
                        id="newDesignation"
                        className="form-control"
                        value={newDesignation}
                        onChange={(e) => setNewDesignation(e.target.value)}
                        placeholder="Enter designation name (e.g., Police Constable, ASI, COMMANDANT)"
                        onKeyPress={(e) => e.key === 'Enter' && (editingDesig ? handleUpdateDesignation() : handleAddDesignation())}
                        style={{ fontSize: '0.875rem' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {editingDesig ? (
                        <>
                          <button type="button" className="btn btn-primary" onClick={handleUpdateDesignation} disabled={loadingDesig}>
                            {loadingDesig ? 'Updating...' : 'Update'}
                          </button>
                          <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={() => { setEditingDesig(null); setNewDesignation(''); }}
                            disabled={loadingDesig}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button type="button" className="btn btn-primary" onClick={handleAddDesignation} disabled={loadingDesig}>
                         {loadingDept ? (
                                  'Adding...'
                                ) : (
                                  <>
                                    <i className="fa-solid fa-plus me-2"></i> Add Designation
                                  </>
                           )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Designation List Table */}
              <div>
                <h4 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>
                  Existing Designations ({designations.length})
                </h4>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Designation Name</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingDesig ? (
                        <tr>
                          <td colSpan="2" style={{ textAlign: 'center', color: '#6c757d', padding: '2rem' }}>
                            Loading designations...
                          </td>
                        </tr>
                      ) : designations.length === 0 ? (
                        <tr>
                          <td colSpan="2" style={{ textAlign: 'center', color: '#6c757d', padding: '2rem' }}>
                            No designations found. Add a designation above to get started.
                          </td>
                        </tr>
                      ) : (
                        designations.map((desig) => (
                          <tr key={desig.id}>
                            <td style={{ fontWeight: 500 }}>{desig.name}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleEditDesignation(desig)}
                                  title="Edit Designation"
                                  disabled={loadingDesig}
                                >
                                <i class="fa-solid fa-pen-to-square"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteDesignation(desig)}
                                  title="Delete Designation"
                                  disabled={loadingDesig}
                                >
                                  <i class="fa-solid fa-trash-can"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                </div>
              </div>
            )}

          {/* Google Sheets Tab */}
          {activeTab === 'google-sheets' && (
            <div className="settings-tab-content">
              <h3>Google Sheets Configuration</h3>
              <p className="settings-description" style={{ marginBottom: '1.5rem', color: '#64748b' }}>
                Configure Google Sheets integration to sync employees from your Google Sheet.
                The sheet should be public or shared with a service account.
              </p>
              <form onSubmit={handleSaveGoogleSheetsConfig} className="settings-form">
                <div className="form-group">
                  <label htmlFor="spreadsheetId">Google Sheet ID or URL *</label>
                  <input
                    type="text"
                    id="spreadsheetId"
                    value={googleSheetsConfig.spreadsheetId}
                    onChange={handleSheetIdChange}
                    placeholder="1mBlfSksYViZWXMyO089p9NsPjw3wbzFsAL45LVOH0to or full URL"
                    required
                    className="form-control"
                  />
                  <small className="form-text" style={{ display: 'block', marginTop: '0.5rem', color: '#64748b', fontSize: '0.875rem' }}>
                    Enter the Sheet ID from the URL: https://docs.google.com/spreadsheets/d/SHEET_ID_HERE/edit
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="gid">Sheet Tab GID (Optional)</label>
                  <input
                    type="text"
                    id="gid"
                    value={googleSheetsConfig.gid}
                    onChange={(e) => setGoogleSheetsConfig(prev => ({ ...prev, gid: e.target.value }))}
                    placeholder="0 (leave empty for default sheet)"
                    className="form-control"
                  />
                  <small className="form-text" style={{ display: 'block', marginTop: '0.5rem', color: '#64748b', fontSize: '0.875rem' }}>
                    GID is found in the URL after #gid= or ?gid=. Leave empty for the first sheet.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="defaultGroupId">Default Group *</label>
                  <select
                    id="defaultGroupId"
                    value={googleSheetsConfig.defaultGroupId || ''}
                    onChange={(e) => setGoogleSheetsConfig(prev => ({ ...prev, defaultGroupId: parseInt(e.target.value) }))}
                    required
                    className="form-control"
                  >
                    <option value="">Select a group</option>
                    {groups.map(group => (
                      <option key={group.id} value={group.id}>
                        {group.name} {group.customer ? `(${group.customer.name})` : ''}
                      </option>
                    ))}
                  </select>
                  <small className="form-text" style={{ display: 'block', marginTop: '0.5rem', color: '#64748b', fontSize: '0.875rem' }}>
                    Employees will be assigned to this group if no company match is found in the sheet.
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="credentialsPath">Service Account Credentials Path (Optional)</label>
                  <input
                    type="text"
                    id="credentialsPath"
                    value={googleSheetsConfig.credentialsPath}
                    onChange={(e) => setGoogleSheetsConfig(prev => ({ ...prev, credentialsPath: e.target.value }))}
                    placeholder="Leave empty for public sheets"
                    className="form-control"
                  />
                  <small className="form-text" style={{ display: 'block', marginTop: '0.5rem', color: '#64748b', fontSize: '0.875rem' }}>
                    Path to service account JSON file (only needed for private sheets). 
                    For public sheets, leave this empty.
                  </small>
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loadingGoogleSheets}
                  >
                    {loadingGoogleSheets ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
